import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { errorSafe } from 'app/infrastructure/redux/error-safe';
import {
  RequestLoadRole, ReceiveLoadRole, FailLoadRole,
  RequestLoadRoleAccess, ReceiveLoadRoleAccess, FailLoadRoleAccess,
  RequestExtraInfo, ReceiveExtraInfo, FailExtraInfo,
  RequestPersonOptions, ReceivePersonOptions, FailPersonOptions,
  RequestAddRole, ReceiveAddRole, FailRoleExecute, ProcessRoleFailed,
  RequestUpdateRole, ReceiveUpdateRole, RequestRoleDelete,
  ClearRoleData, UpdateAccessData, ClearValidation,
  RequestSetDefaultRole, ReceiveSetDefaultRole, FailSetDefaultRole,
  RequestDefaultRole, ReceiveDefaultRole, FailDefaultRole, ChangeDefaultRoleMark
} from './action-types';
import { api } from './api';
import { editApi } from '../api';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { STATE } from 'app/constants';
import * as simpleFormActions from 'app/infrastructure/simple-form/actions';
import { administrationEditTranslations as text } from 'app/area/administration/edit/constants';
import { SuccessNotification, ErrorNotification } from 'app/infrastructure/notifications/user-messages/action-types';
import * as navigate from 'app/area/shared/navigation';
import { sharedApi } from 'app/area/administration/shared/api';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { AccessItem, AccessPanelDataValidation } from 'app/components/access-panel/shared/types';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { normalizeValidation } from 'app/components/access-panel/validation';
import { Action, AnyAction } from 'redux';
import { QueryParam, SetDefaultRole } from 'app/area/administration/edit/role/types';
import { dispatchSaveError } from 'app/area/administration/edit/role/actions.util';
import { mapFromDefaultRole, mapToDefaultRole } from 'app/area/administration/edit/user/default-role-mapper';
import { DefaultRole } from 'app/area/administration/shared/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

const simpleFormActionCreators = simpleFormActions.actionCreators(STATE.administrationRoleEdit);

export const actionCreators = {
  loadRole: (id: number, updatedData: { [key: string]: string } = {}): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: any) => {
      try {
        dispatch(new RequestLoadRole());

        const result = await api.getRole(id);

        dispatch(new ReceiveLoadRole({ ...result.data, ...updatedData }));
      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationRoleEdit,
            translationId: text.no_access_to_role_administration
          });
        } else {
          err && err.userMessages && dispatch(new ErrorNotification(err.userMessages));

          navigate.toAdminRoleList();
        }

        dispatch(new FailLoadRole());
      }
    },

  loadRoleAccess: (id: number): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: any) => {
      try {
        dispatch(new RequestLoadRoleAccess());

        const result = await sharedApi.roleAccess(id);

        result && dispatch(new ReceiveLoadRoleAccess(result[0]));
      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.administrationRoleEdit,
            translationId: text.no_access_to_role_administration
          });
        }

        dispatch(new FailLoadRoleAccess());

      }
    },

  loadExtraInfo: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestExtraInfo());
      try {
        const extraInfo = await editApi.getExtraInfo();
        dispatch(new ReceiveExtraInfo(extraInfo.data));
      } catch (err) {
        dispatch(new FailExtraInfo());
      }
    },

  loadPersonOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new RequestPersonOptions());
      try {
        const personOptions = await editApi.getPersonOptions();
        dispatch(new ReceivePersonOptions(personOptions.rootCodes));
      } catch (err) {
        dispatch(new FailPersonOptions());
      }
    },

  addRole: (query: QueryParam, setDefaultRoleData: SetDefaultRole): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationRoleEdit.texts);

      try {
        dispatch(new RequestAddRole());
        const result = await api.addRole(query);

        if (result) {
          dispatch(new ReceiveAddRole());
          dispatch(simpleFormActionCreators.resetChangesListener());
          dispatch(new SuccessNotification(translate(text.new_role_msg)));

          const roleId = result.data?.id;

          if (isDefinedIncludingZero(roleId)) {

            const { markedAsDefaultRole, translation } = setDefaultRoleData;

            if (!markedAsDefaultRole) {
              navigate.toAdminRoleList();
              return;
            }

            dispatch(new RequestSetDefaultRole());

            try {
              await sharedApi.setDefaultRole(mapToDefaultRole(markedAsDefaultRole ? { value: roleId } : {} as DefaultRole));
              dispatch(new ReceiveSetDefaultRole());
              dispatch(new SuccessNotification(markedAsDefaultRole
                ? translation.mark_as_default_role_success : translation.remove_default_role_success));
            } catch (err) {
              dispatch(new ErrorNotification(markedAsDefaultRole
                ? translation.mark_as_default_role_failure : translation.remove_default_role_failure));
              dispatch(new FailSetDefaultRole());
            } finally {
              navigate.toAdminRoleList();
            }
          } else {
            navigate.toAdminRoleList();
          }
        }
      } catch (err) {
        dispatch(new FailRoleExecute());
        dispatch(new ErrorNotification(translate(text.validation_error)));
        dispatch(new ProcessRoleFailed(err.validationErrors
          ? complexValidationFromServer(normalizeValidation(err.validationErrors, query)) : []));
      }
    },

  updateRole: (id: number, query: QueryParam, setDefaultRoleData: SetDefaultRole): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationRoleEdit.texts);

      try {
        dispatch(new RequestUpdateRole());
        const result = await api.updateRole(id, query);

        if (result) {
          dispatch(new ReceiveUpdateRole());
          dispatch(simpleFormActionCreators.resetChangesListener());
          dispatch(new SuccessNotification(translate(text.role_updated)));

          const { currentDefaultRole: defaultRole, markedAsDefaultRole, roleId, translation } = setDefaultRoleData;

          const shouldSetDefaultRole = (isDefinedIncludingZero(defaultRole?.value) && isDefinedIncludingZero(roleId)
            && defaultRole.value === roleId && !markedAsDefaultRole) ||
            (isDefinedIncludingZero(defaultRole?.value) && isDefinedIncludingZero(roleId)
              && defaultRole.value !== roleId && markedAsDefaultRole);

          if (!shouldSetDefaultRole) {
            navigate.toAdminRoleList();
            return;
          }

          dispatch(new RequestSetDefaultRole());

          try {
            await sharedApi.setDefaultRole(mapToDefaultRole(markedAsDefaultRole ? { value: roleId } : {} as DefaultRole));
            dispatch(new ReceiveSetDefaultRole());
            dispatch(new SuccessNotification(markedAsDefaultRole
              ? translation.mark_as_default_role_success : translation.remove_default_role_success));
          } catch (err) {
            dispatch(new ErrorNotification(markedAsDefaultRole
              ? translation.mark_as_default_role_failure : translation.remove_default_role_failure));
            dispatch(new FailSetDefaultRole());
          } finally {
            navigate.toAdminRoleList();
          }
        } else {
          navigate.toAdminRoleList();
        }
      } catch (err) {
        dispatch(new FailRoleExecute());

        if (isNotFoundStatus(err)) {
          navigate.toAdminRoleList();
        }

        dispatchSaveError(err, dispatch, translate(text.validation_error), query, getState);
      }
    },

  duplicateRole: (query: QueryParam, setDefaultRoleData: SetDefaultRole): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationRoleEdit.texts);

      try {
        dispatch(new RequestAddRole());
        const result = await api.addRole(query);

        if (result) {
          dispatch(new ReceiveAddRole());
          dispatch(simpleFormActionCreators.resetChangesListener());
          dispatch(new SuccessNotification(translate(text.duplicate_role)));

          const roleId = result.data?.id;

          if (isDefinedIncludingZero(roleId)) {

            const { markedAsDefaultRole, translation } = setDefaultRoleData;

            if (!markedAsDefaultRole) {
              navigate.toAdminRoleList();
              return;
            }

            dispatch(new RequestSetDefaultRole());

            try {
              await sharedApi.setDefaultRole(mapToDefaultRole(markedAsDefaultRole ? { value: roleId } : {} as DefaultRole));
              dispatch(new ReceiveSetDefaultRole());
              dispatch(new SuccessNotification(markedAsDefaultRole
                ? translation.mark_as_default_role_success : translation.remove_default_role_success));
            } catch (err) {
              dispatch(new ErrorNotification(markedAsDefaultRole
                ? translation.mark_as_default_role_failure : translation.remove_default_role_failure));
              dispatch(new FailSetDefaultRole());
            } finally {
              navigate.toAdminRoleList();
            }
          }
        } else {
          navigate.toAdminRoleList();
        }
      } catch (err) {
        dispatch(new FailRoleExecute());
        dispatch(new ErrorNotification(translate(text.validation_error)));
        dispatch(new ProcessRoleFailed(err.validationErrors
          ? complexValidationFromServer(normalizeValidation(err.validationErrors, query)) : []));
      }
    },

  deleteRole: (id: number): ActionDispatching<AnyAction, ApplicationState> => errorSafe(
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationRoleEdit.texts);
      await sharedApi.deleteRole(id);
      dispatch(new RequestRoleDelete());
      navigate.toAdminRoleList();
      dispatch(new SuccessNotification(translate(text.role_successfully_deleted)));
    }),

  clearValueInStore: (id: string): ActionDispatching<AnyAction, ApplicationState> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(simpleFormActionCreators.clearValueInStore(id));
    }),

  clearAllValues: (): ActionDispatching<AnyAction, ApplicationState> => async (dispatch: any, getState: any) => {
    dispatch(new ClearRoleData());
    dispatch(simpleFormActionCreators.clearAllValues());
  },

  clearValidation: (formName: string, parameters?: { [key: string]: number | string }): ActionDispatching<Action, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new ClearValidation(formName, parameters));
    },

  updateAccessData: (key: string, accessItem: Partial<AccessItem>): ActionDispatching<Action, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      dispatch(new UpdateAccessData(key, accessItem));
    },

  checkAccess: (hasAccess: boolean): ActionDispatching<Action, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      if (!hasAccess) {
        blockAccess({
          dispatch,
          getState,
          area: STATE.administrationRoleEdit,
          translationId: text.no_access_to_role_administration
        });
      }
    },

  validateCustomData: (accessPanelDataValidation: AccessPanelDataValidation): ActionDispatching<Action, ApplicationState> =>
    async (dispatch: any, getState: () => ApplicationState) => {
      const translate = createDescriptionSelector(getState().administrationRoleEdit.texts);
      dispatch(new ErrorNotification(translate(text.validation_error)));
      dispatch(new ProcessRoleFailed(accessPanelDataValidation || []));
      dispatch(new FailRoleExecute());
    },

  loadDefaultRole: (roleId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestDefaultRole());
      try {
        const defaultRoleDto = await sharedApi.getDefaultRole();
        const defaultRole = mapFromDefaultRole(defaultRoleDto);
        const markedAsDefaultRole = isDefinedIncludingZero(roleId) && isDefinedIncludingZero(defaultRole?.value)
          ? defaultRole.value === roleId : false;
        dispatch(new ReceiveDefaultRole(defaultRole, markedAsDefaultRole));
      } catch (err) {
        dispatch(new FailDefaultRole());
      }
    },

  changeDefaultRoleMark: (mark: boolean): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new ChangeDefaultRoleMark(mark));
    }
};
